import React, { useCallback } from 'react';
import { useOnboardingStore } from '../stores/onboarding-store';
import ProductSelectionForm from './ProductSelectionForm';
import LocationSelectionForm from './LocationSelectionForm';
import DocumentUploadForm from './DocumentUploadForm';
import PhotoSignatureUpload from './PhotoSignatureUpload';
import IndividualReviewForm from './IndividualReviewForm';
import {GroupedFieldConfigurationDTO, CustomerCategoryProductDTO, CustomerCategoryLocationDTO, CustomerCategoryDocumentDTO} from "@/shared/api/model";
import SingleFieldGroupForm from './SingleFieldGroupForm';

const CUSTINFO_FIELDS = new Set(['ctitle', 'marital', 'housing', 'educatio', 'language', 'income', 'officer', 'kin1', 'kin2', 'relationshipManager']);

interface IndividualOnboardingFormProps {
  categoryId: number;
  currentStep: number;
  fieldConfiguration?: GroupedFieldConfigurationDTO;
  categoryProducts?: CustomerCategoryProductDTO[]; // Products from parent
  categoryLocations?: CustomerCategoryLocationDTO[]; // Locations from parent
  categoryDocuments?: CustomerCategoryDocumentDTO[]; // Documents from parent
}

const IndividualOnboardingForm: React.FC<IndividualOnboardingFormProps> = ({
  categoryId,
  currentStep,
  categoryProducts,
  categoryLocations,
  categoryDocuments,
}) => {
  const {
    formData,
    updateFieldValue,
    updateFormData,
    errors,
    steps,
  } = useOnboardingStore();

  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    updateFieldValue(CUSTINFO_FIELDS.has(fieldName) ? 'custInfoData' : 'customerData', fieldName, value);
  }, [updateFieldValue]);

  const combinedFormData = {
    ...formData.customerData,
    ...formData.custInfoData,
  };

  const handleProductsChange = useCallback((products: any[]) => {
    updateFormData('productData', products);
  }, [updateFormData]);

  const handleLocationsChange = useCallback((locations: any[]) => {
    updateFormData('locationData', locations);
  }, [updateFormData]);

  const handleDocumentsChange = useCallback((documents: any[]) => {
    updateFormData('documentData', documents);
  }, [updateFormData]);

  const handlePhotoChange = useCallback((fileId: string, uploadedAt: string) => {
    updateFormData('photoFileId', fileId);
    updateFormData('photoUploadedAt', uploadedAt);
  }, [updateFormData]);

  const handleSignatureChange = useCallback((fileId: string, uploadedAt: string) => {
    updateFormData('signatureFileId', fileId);
    updateFormData('signatureUploadedAt', uploadedAt);
  }, [updateFormData]);

  const handlePhotoRemove = useCallback(() => {
    updateFormData('photoFileId', undefined);
    updateFormData('photoUploadedAt', undefined);
  }, [updateFormData]);

  const handleSignatureRemove = useCallback(() => {
    updateFormData('signatureFileId', undefined);
    updateFormData('signatureUploadedAt', undefined);
  }, [updateFormData]);

  const renderStepContent = () => {
    const currentStepData = steps[currentStep];
    
    if (!currentStepData) {
      return <div>Step not found</div>;
    }

    // Handle field group steps
    if (currentStepData.component === 'FieldGroup' && currentStepData.fieldGroup) {
      return (
        <SingleFieldGroupForm
          fieldGroup={currentStepData.fieldGroup}
          formData={combinedFormData}
          onFieldChange={handleFieldChange}
          errors={errors}
          categoryId={categoryId}
        />
      );
    }

    // Handle other component types
    switch (currentStepData.component) {
      case 'ProductSelectionForm':
        return (
          <ProductSelectionForm
            categoryProducts={categoryProducts || []}
            selectedProducts={formData.productData}
            onProductsChange={handleProductsChange}
            errors={errors}
          />
        );

      case 'DocumentUploadForm':
        return (
          <DocumentUploadForm
            categoryDocuments={categoryDocuments || []}
            selectedDocuments={formData.documentData}
            onDocumentsChange={handleDocumentsChange}
            errors={errors}
          />
        );

      case 'LocationForm':
        return (
          <LocationSelectionForm
            categoryLocations={categoryLocations || []}
            selectedLocations={formData.locationData}
            onLocationsChange={handleLocationsChange}
            errors={errors}
          />
        );

      case 'PhotoSignatureUpload':
        return (
          <PhotoSignatureUpload
            photoFileId={formData.photoFileId}
            signatureFileId={formData.signatureFileId}
            onPhotoChange={handlePhotoChange}
            onSignatureChange={handleSignatureChange}
            onPhotoRemove={handlePhotoRemove}
            onSignatureRemove={handleSignatureRemove}
          />
        );

      case 'ReviewForm':
        return (
          <IndividualReviewForm
            categoryId={categoryId}
            categoryProducts={categoryProducts}
            categoryLocations={categoryLocations}
            categoryDocuments={categoryDocuments}
          />
        );

      default:
        return <div>Unknown component: {currentStepData.component}</div>;
    }
  };

  return (
    <div className="individual-onboarding-form">
      {renderStepContent()}
    </div>
  );
};

export default IndividualOnboardingForm;
